import { ActionType, BallAction, FrameStencil, MarkerPlayer } from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestStencil } from "../find-closest";
import {
  getBallActions,
  getBallMarkers,
  getHandoffActions,
  getNonAssociatedPlayerAndPossessionActions,
} from "../frame-stencil-filters";
import { refreshMovements } from "../refresh-movements";

export interface AddPlayerProps {
  player: MarkerPlayer;
  stencils: FrameStencil[];
}

export const addPlayer = ({ player, stencils }: AddPlayerProps): FrameStencil[] => {
  /** get non-associated player actions, non-associated possession actions
   * find closest action */
  const closestAction = findClosestStencil({
    target: { id: player.id, point: player.pos },
    stencils: getNonAssociatedPlayerAndPossessionActions(stencils, true),
  });

  /** add closest action to player's movements */
  const updatedPlayer: MarkerPlayer = { ...player, ...(closestAction && { movements: [{ id: closestAction?.id }] }) };

  const playerHasPossession =
    getBallMarkers(stencils).find((b) => b.associatedPlayer === updatedPlayer.id) !== undefined;

  const closestPassStart = findClosestStencil({
    target: { id: player.id, point: player.pos },
    stencils: [...getBallActions(stencils), ...(playerHasPossession ? getHandoffActions(stencils) : [])].filter(
      (pass) => !pass.prevPlayer
    ),
  });

  const stencilsWithUpdatedPassStart =
    closestPassStart && closestPassStart.actionType === ActionType.BALL
      ? sliceItemUpdate({ item: { ...closestPassStart, prevPlayer: player.id }, arr: stencils })
      : closestPassStart && closestPassStart.actionType === ActionType.HANDOFF
      ? sliceItemUpdate({
          item: { ...closestPassStart, prevPlayer: player.id, startPos: player.pos },
          arr: stencils,
        })
      : stencils;

  /** get any pass action endpoints */
  const closestPassEnd = findClosestStencil({
    target: { id: player.id, point: player.pos },
    stencils: [...getBallActions(stencils), ...(!playerHasPossession ? getHandoffActions(stencils) : [])].filter(
      (pass) => !pass.nextPlayer
    ),
    stencilsPointAccessor: (stencil) => (stencil.actionType === ActionType.BALL ? stencil.endPos : stencil.pos),
  });

  // if on pass endpoint, add player to pass' nextPlayer
  const stencilsWithUpdatedPassEnd =
    closestPassEnd && closestPassEnd.actionType === ActionType.BALL
      ? sliceItemUpdate({ item: { ...closestPassEnd, nextPlayer: player.id }, arr: stencilsWithUpdatedPassStart })
      : closestPassEnd && closestPassEnd.actionType === ActionType.HANDOFF
      ? sliceItemUpdate({
          item: { ...closestPassEnd, nextPlayer: player.id, endPos: player.pos },
          arr: stencilsWithUpdatedPassStart,
        })
      : stencilsWithUpdatedPassStart;

  /** check if the new player's movements lie on any playerActionEnd to associate pass */
  const passWithPlayerActionEnd: BallAction | null =
    getBallActions(stencilsWithUpdatedPassEnd).find((ball) => ball.playerActionEnd === closestAction?.id) || null;

  // if closestAction on pass playerActionEnd, add player to pass' nextPlayer
  const stencilsWithFoundPlayerActionEnd = passWithPlayerActionEnd
    ? sliceItemUpdate({ item: { ...passWithPlayerActionEnd, nextPlayer: player.id }, arr: stencilsWithUpdatedPassEnd })
    : stencilsWithUpdatedPassEnd;

  return refreshMovements([...stencilsWithFoundPlayerActionEnd, updatedPlayer]);
};
